import ValidateModule from "./ValidateModule";

import type { IEventChannel } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type {ApiOpts} from "@esWebAPI/es/model/Models";
import type CacheModule from "./CacheModule";

const es = global.es;

/**
 * @class DataStoreModule
 */
export default class DataStoreModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null; // prevent memory leaks when the map widget is destroyed
        });

        this.get = this.get.bind(this);
        this.getByIds = this.getByIds.bind(this);
        this.list = this.list.bind(this);
        this.count = this.count.bind(this);
        this.listByLayerViewRelevance = this.listByLayerViewRelevance.bind(this);
        this.listDefaultAnalysisRegions = this.listDefaultAnalysisRegions.bind(this);
        this.create = this.create.bind(this);
        this.update = this.update.bind(this);
        this.remove = this.remove.bind(this);
        this.layerMetaFromDataStoreItem = this.layerMetaFromDataStoreItem.bind(this);
        this.layerMetaFromDataStoreItemTitle = this.layerMetaFromDataStoreItemTitle.bind(this);
        this.layerMetaFromDataStoreItemLayerMetaId = this.layerMetaFromDataStoreItemLayerMetaId.bind(this);
    }
    
    /**
     * Get the resource from the server
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    get(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, id, opts);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param ids
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    getByIds(ids: number[], opts?: ApiOpts) {
        if (!ValidateModule.checkIds(ids)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/ids?ids=" + es.util.url.stringify(ids);
        return this.cache.doGet(this.rqBroker, this.resourceName, url, ids, opts);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    list(criteria, sortOrder, offset: number, length: number, opts?: ApiOpts) {
        //return rqBroker.doGet({url: es.api.getListQueryURL(resourceName, criteria, sortOrder, offset, length)});
        return this.cache.doGetList(this.rqBroker, this.resourceName, criteria, sortOrder, offset, length, opts);
    }
    
    /**
     * Get a count of resources matching the given Criteria
     * 
     * @param criteria
     * 
     * @returns long
     */
    count(criteria, opts?: ApiOpts) {
        let url = es.api.getURL(this.resourceName) + "/count";
        if (criteria) {
            url += "?criteria=" + es.util.url.stringify(criteria);
        }

        return this.rqBroker.doGet({url: url, opts: opts});
    }
    
    /**
     * Get the resource from the server
     * 
     * @param id
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    listByLayerViewRelevance(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/list_by_layer_view_relevance/" + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "listByLayerViewRelevance_" + id, opts);
    }

    /**
     * Gets default regions datasets available on the datastore.
     * 
     * ATTENTION: Might be required to import them from datastore to use them.
     */
    listDefaultAnalysisRegions(opts?: ApiOptsts) {
        const url = es.api.getURL(this.resourceName) + '/list_default_analysis_regions';
        return this.cache.doGet(this.rqBroker, this.resourceName, url, 'listDefaultAnalysisRegions', opts);
    }
    
    /**
     * POST a new resource to the server (note: id attrib must be -1)
     * 
     * @param resourceObj e.g. {"id":-1,"region":"","title":"US States (Contiguous)","description":"US States (Contiguous)","layerMetaId":102,"profile":1}]
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    create(resourceObj, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }
        
        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName),
            opts: opts,
            data: resourceObj
        }).done((rs) => {
            this.cache?.setEntity(this.resourceName, rs);
        });
    }
    
    /**
     * PUT a new resource to the server (note: id attrib must be valid)
     * 
     * @param resourceObj e.g. 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    update(resourceObj, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }
        
        return this.rqBroker.doPut({
            url: es.api.getURL(this.resourceName) + "/" + resourceObj.id,
            opts: opts,
            data: resourceObj
        }).done((rs) => {
            this.cache?.setEntity(this.resourceName, rs);
        });
    }
    
    /**
     * Remove the resource from the server
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    remove(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + id;
        return this.rqBroker.doDelete({url: url, opts: opts}).done((rs) => {
            this.cache?.clear(this.resourceName, id);
        });
    }
    
    /**
     * Get the resource from the server
     * 
     * @param id datasetore e.g. 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    layerMetaFromDataStoreItem(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName) + "/add",
            opts: opts,
            data: id
        }).done((rs) => {
            this.cache?.clear("LayerMeta");
        });
    }
    
    /**
     * Get the resource from the server
     * 
     * @param id datasetore  e.g. 
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    layerMetaFromDataStoreItemTitle(dataStoreItemTitle: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(dataStoreItemTitle)) {
            return es.util.deferred.rejected();
        }

        const criteria = {
            $type: "Equal",
            attr: "title",
            value: dataStoreItemTitle
        };
        
        return this.list(criteria, null, 0, 1, opts).then((rs) => {
            return this.layerMetaFromDataStoreItem(rs[0].id, opts);
        });
    }
    
    /**
     * Get the resource from the server
     * 
     * @param dataStoreItemLayerMetaId
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    layerMetaFromDataStoreItemLayerMetaId(dataStoreItemLayerMetaId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(dataStoreItemLayerMetaId)) {
            return es.util.deferred.rejected();
        }

        const criteria = {
            $type: "Equal",
            attr: "layerMetaId",
            value: dataStoreItemLayerMetaId
        };
        
        return this.list(criteria, null, 0, 1, opts).then((rs) => {
            return this.layerMetaFromDataStoreItem(rs[0].id, opts);
        });
    }
}
